import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = "The Hills Festival | Music & Cultural Festival in Meghalaya",
  description = "Join The Hills Festival 2024 - A vibrant celebration of music, culture, and eco-consciousness in Meghalaya. Featuring 20+ global artists set against the breathtaking beauty of Umiam Lake. Experience tradition, modernity, and sustainability.",
  keywords = "Hills Festival, Meghalaya, Music Festival, Cultural Festival, Umiam Lake, Eco-conscious Festival, Global Artists, Meghalaya Tourism, Festival 2024, Music Events India",
  image = "/logo512.png",
  url = "https://thehillsfestival.in",
  type = "website"
}) => {
  const siteName = "The Hills Festival";
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  const fullImageUrl = image.startsWith('http') ? image : `${url}${image}`;

  // Structured Data (JSON-LD) for Event
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "name": "The Hills Festival 2024",
    "description": description,
    "startDate": "2024-12-06T00:00:00+05:30",
    "endDate": "2024-12-07T23:59:59+05:30",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "Umiam Lake, Meghalaya",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Meghalaya",
        "addressCountry": "IN"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "The Hills Festival",
      "url": url,
      "email": "MANAGEMENT@THEHILLSFESTIVAL.IN"
    },
    "image": fullImageUrl,
    "offers": {
      "@type": "Offer",
      "url": url,
      "availability": "https://schema.org/InStock",
      "price": "0",
      "priceCurrency": "INR"
    },
    "performer": [
      {
        "@type": "MusicGroup",
        "name": "I Built The Sky (AUS)"
      },
      {
        "@type": "MusicGroup",
        "name": "Tiana Khasi (AUS)"
      },
      {
        "@type": "MusicGroup",
        "name": "Yung Sammy (NG/IND)"
      },
      {
        "@type": "MusicGroup",
        "name": "Lis Vegas (USA)"
      },
      {
        "@type": "MusicGroup",
        "name": "Robu (IND/UK)"
      }
    ]
  };

  // Organization Structured Data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The Hills Festival",
    "url": url,
    "logo": fullImageUrl,
    "sameAs": [
      // Add social media links here when available
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "MANAGEMENT@THEHILLSFESTIVAL.IN"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="The Hills Festival" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${siteName} - ${description.substring(0, 100)}`} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={`${siteName} - ${description.substring(0, 100)}`} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#282828" />
      <meta name="msapplication-TileColor" content="#282828" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Geo Tags */}
      <meta name="geo.region" content="IN-ML" />
      <meta name="geo.placename" content="Meghalaya" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
    </Helmet>
  );
};

export default SEO;
